import { Fragment, lazy, type FC } from 'react';
import { Navigate, Route, useLocation, useMatch } from 'react-router';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { PageHeader } from 'src/components/layout/page-header';
import { Link } from 'src/components/navigation/link';
import { Tabs } from 'src/components/navigation/tabs';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { useBidCollectorRoot } from 'src/pages/snam-bid-collector/use-bid-collector';

const LocationsOverviewPage = lazy(() => import('./locations-overview'));
const BidsOverviewPage = lazy(() => import('./bids-overview'));

export const SnamBidCollector: FC = () => {
  const bidCollectorRoot = useBidCollectorRoot();

  const { pathname } = useLocation();
  const matchesTransport = useMatch('/transport/snam-bid-collector/*');
  const isNewDesignActive = useIsNewDesignActive();

  // redirect to old location
  if (matchesTransport && !isNewDesignActive)
    return <Navigate replace to={pathname.replace('/transport', '')} />;

  // redirect to new location
  if (!matchesTransport && isNewDesignActive)
    return <Navigate replace to={`/transport${pathname}`} />;

  if (!bidCollectorRoot.response) {
    return <PageSpinner />;
  }

  if (!bidCollectorRoot.response.data._links.toggleFeature)
    return <ThrowNotFound />;

  return (
    <div className="grid gap-xl">
      <PageHeader title="Bid Collector" />

      <Tabs>
        <Link
          to={
            isNewDesignActive
              ? '/transport/snam-bid-collector/settings'
              : '/snam-bid-collector/settings'
          }
        >
          Settings
        </Link>
        <Link
          to={
            isNewDesignActive
              ? '/transport/snam-bid-collector/bids'
              : '/snam-bid-collector/bids'
          }
        >
          Bids
        </Link>
      </Tabs>

      <RootRoutes>
        {['/snam-bid-collector', '/transport/snam-bid-collector'].map(
          (basePath) => (
            <Fragment key={basePath}>
              <Route
                path={basePath}
                element={<Navigate replace to={`${basePath}/settings`} />}
              />

              <Route
                path={`${basePath}/settings`}
                element={
                  <LocationsOverviewPage bidCollectorRoot={bidCollectorRoot} />
                }
              />

              <Route
                path={`${basePath}/bids`}
                element={
                  <BidsOverviewPage bidCollectorRoot={bidCollectorRoot} />
                }
              />
            </Fragment>
          )
        )}

        <Route path="*" element={<ThrowNotFound />} />
      </RootRoutes>
    </div>
  );
};
